import React from "react";
import { Alert, Form } from "react-bootstrap";

export const FILE_OPTION = "file";
export const UNKNOWN_OPTION = "unknown";
const UNKNOWN_OPTION_LABEL = "Current prompt (version unknown)";
export const VERSIONS_STATUS = { loading: "loading", loaded: "loaded", failed: "failed" };
export const VERSIONS_LOADING = { status: VERSIONS_STATUS.loading, versions: [], total: 0, detail: null };
const LIST_PLACEHOLDER = {
  [VERSIONS_STATUS.loading]: "Loading versions…",
  [VERSIONS_STATUS.failed]: "Versions unavailable",
};
const FILE_OPTION_LABEL = "Repository file (fallback)";
const UNKNOWN_REASON = "unknown reason";
const FALLBACK_REASON_TEXT = {
  not_configured: "Langfuse is not configured on this server",
  unreachable: "Langfuse could not be reached",
  no_production_version: "no version is labelled production",
  not_text: "the production version is a chat prompt",
};

const fallbackNotice = (reason) =>
  `Langfuse prompt unavailable (${FALLBACK_REASON_TEXT[reason] || UNKNOWN_REASON}).`
  + " The prompt below is the repository file session_summary_v3.txt.";

const SEPARATOR = " · ";
const CHAT_TYPE = "chat";
const CHAT_SUFFIX = " (chat, cannot load)";

const datePart = (createdAt) => (createdAt ? createdAt.slice(0, "YYYY-MM-DD".length) : null);

const isChat = (row) => row.type === CHAT_TYPE;

export const versionOptionLabel = (row) => {
  const { version, labels, created_at: createdAt } = row;
  const text = [`v${version}`, labels?.length ? labels.join(", ") : null, datePart(createdAt)]
    .filter(Boolean)
    .join(SEPARATOR);
  return isChat(row) ? `${text}${CHAT_SUFFIX}` : text;
};

// The prefill's version must stay visible even when the list stops before it.
const withPrefillVersion = (versions, prefillVersion) => {
  if (!prefillVersion || versions.some((row) => row.version === prefillVersion.version)) {
    return versions;
  }
  return [{ version: prefillVersion.version, labels: prefillVersion.labels }, ...versions];
};

const EMPTY_VALUE = "—";
const NO_CONFIG = "none";
const JSON_INDENT = 2;

const listOrEmpty = (items) => (items?.length ? items.join(", ") : EMPTY_VALUE);

const configText = (config) =>
  config && Object.keys(config).length ? JSON.stringify(config, null, JSON_INDENT) : NO_CONFIG;

const createdText = (createdAt) => (createdAt ? new Date(createdAt).toLocaleString() : EMPTY_VALUE);

const VersionDetails = ({ row }) => (
  <dl style={{ display: "grid", gap: "4px 16px", gridTemplateColumns: "max-content 1fr", margin: "8px 0 0" }}>
    <dt>Labels</dt>
    <dd style={{ margin: 0 }}>{listOrEmpty(row.labels)}</dd>
    <dt>Tags</dt>
    <dd style={{ margin: 0 }}>{listOrEmpty(row.tags)}</dd>
    <dt>Commit message</dt>
    <dd style={{ margin: 0 }}>{row.commit_message || EMPTY_VALUE}</dd>
    <dt>Author</dt>
    <dd style={{ margin: 0 }}>{row.created_by || EMPTY_VALUE}</dd>
    <dt>Created</dt>
    <dd style={{ margin: 0 }}>{createdText(row.created_at)}</dd>
    <dt>Config</dt>
    <dd style={{ margin: 0 }}><pre style={{ margin: 0 }}>{configText(row.config)}</pre></dd>
  </dl>
);

const ALERT_STYLE = { padding: "8px", marginBottom: 0, marginTop: "8px" };

const VersionOptions = ({ rows, fromFile, versionUnknown }) => (
  <>
    {fromFile && <option value={FILE_OPTION} disabled>{FILE_OPTION_LABEL}</option>}
    {versionUnknown && <option value={UNKNOWN_OPTION} disabled>{UNKNOWN_OPTION_LABEL}</option>}
    {rows.map((row) => (
      <option key={row.version} value={row.version} disabled={isChat(row)}>
        {versionOptionLabel(row)}
      </option>
    ))}
  </>
);

export const PromptVersionPicker = ({
  versionList,
  prefillVersion,
  selectedVersion,
  loadingVersion,
  loadError,
  edited,
  fromFile,
  showFallbackNotice,
  versionUnknown,
  fallbackReason,
  onPick,
}) => {
  const { status, versions, total: totalVersions, detail } = versionList;
  const placeholder = LIST_PLACEHOLDER[status];
  const rows = withPrefillVersion(versions, prefillVersion);
  const selectedRow = rows.find((row) => row.version === selectedVersion);
  return (
    <Form.Group controlId="prompt_version">
      {showFallbackNotice && (
        <Alert style={{ ...ALERT_STYLE, marginBottom: "8px", marginTop: 0 }} variant="warning">
          {fallbackNotice(fallbackReason)}
        </Alert>
      )}
      <Form.Label style={{ fontWeight: "bold" }}>Prompt version</Form.Label>
      <div style={{ alignItems: "center", display: "flex", gap: "12px" }}>
        <Form.Select
          name="prompt_version"
          value={placeholder ? "" : selectedVersion ?? ""}
          disabled={Boolean(placeholder) || loadingVersion !== null}
          onChange={(e) => onPick(Number(e.target.value))}
        >
          {placeholder
            ? <option value="">{placeholder}</option>
            : <VersionOptions rows={rows} fromFile={fromFile} versionUnknown={versionUnknown} />}
        </Form.Select>
        {loadingVersion !== null && (
          <span style={{ whiteSpace: "nowrap" }}>{`Loading version ${loadingVersion}…`}</span>
        )}
        {edited && <span className="text-muted">(edited)</span>}
      </div>
      {totalVersions > versions.length && (
        <Form.Text className="text-muted">
          {`Showing the newest ${versions.length} of ${totalVersions} versions.`}
        </Form.Text>
      )}
      {status === VERSIONS_STATUS.failed && (
        <Alert style={ALERT_STYLE} variant="warning">
          {`Could not list Langfuse versions: ${detail}`}
        </Alert>
      )}
      {selectedRow && <VersionDetails row={selectedRow} />}
      {loadError && (
        <Alert style={ALERT_STYLE} variant="danger">
          {`Could not load version ${loadError.version}: ${loadError.detail}`}
        </Alert>
      )}
    </Form.Group>
  );
};
